import React, { useState, useEffect } from 'react';
import DashboardLayout from './DashboardLayout';
import {
  getServices,
  searchClients,
  createClient,
  getClientVehicles,
  createVehicle,
  createOrder,
  checkoutOrder,
  getTodayOrders,
  updateOrderStatus,
  deleteOrder,
  getPettyCashBalance,
  getPettyCashMovements,
  createPettyCashMovement,
  getClientRewards,
} from '../lib/api';
import { formatCurrency, getVehicleTypeLabel } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Search, Plus, Trash2, CreditCard, Car, User, Phone, Star,
  Loader2, CheckCircle, Receipt, X, RefreshCw, Wallet, Gift,
} from 'lucide-react';
import { toast } from 'sonner';

import NewClientModal from '../components/pos/NewClientModal';
import NewVehicleModal from '../components/pos/NewVehicleModal';
import CheckoutModal from '../components/pos/CheckoutModal';
import TicketModal from '../components/pos/TicketModal';
import PettyCashModal from '../components/pos/PettyCashModal';
import OrdersTable from '../components/pos/OrdersTable';
import CashSessionBar from '../components/pos/CashSessionBar';
import WalkInModal from '../components/pos/WalkInModal';
import { useAuth } from '../contexts/AuthContext';
import { getBranches } from '../lib/api';

export default function POSPage() {
  const { isAdmin, user } = useAuth();
  const [services, setServices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serviceCategory, setServiceCategory] = useState('all');

  // Current order state
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  // Multi-vehicle: extra vehicles already added to the ticket
  const [ticketVehicles, setTicketVehicles] = useState([]);
  const [notes, setNotes] = useState('');
  const [estimatedTime, setEstimatedTime] = useState('');

  // Search/Create client
  const [clientSearch, setClientSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [newClientData, setNewClientData] = useState({ name: '', phone: '', email: '' });

  // Vehicles
  const [clientVehicles, setClientVehicles] = useState([]);
  const [showNewVehicleModal, setShowNewVehicleModal] = useState(false);
  const [newVehicleData, setNewVehicleData] = useState({ type: 'sedan', model: '', plates: '', color: '' });

  // Checkout
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('efectivo');
  const [discount, setDiscount] = useState(0);
  const [pointsToRedeem, setPointsToRedeem] = useState(0);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Rewards
  const [clientRewards, setClientRewards] = useState([]);
  const [selectedReward, setSelectedReward] = useState(null);

  // Ticket
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketData, setTicketData] = useState(null);

  // Petty Cash
  const [showPettyCashModal, setShowPettyCashModal] = useState(false);
  const [pettyCashBalance, setPettyCashBalance] = useState(0);
  const [pettyCashMovements, setPettyCashMovements] = useState([]);
  const [pettyCashLoading, setPettyCashLoading] = useState(false);
  const [pettyCashForm, setPettyCashForm] = useState({
    type: 'ingreso', amount: '', concept: '', notes: '',
  });

  // Cash session state
  const [cashSession, setCashSession] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(''); // admin picks; cajero auto

  // Walk-in modal
  const [showWalkInModal, setShowWalkInModal] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAdmin) return;
    getBranches()
      .then((res) => {
        setBranches(res.data);
        if (res.data.length > 0) setSelectedBranch(res.data[0].id);
      })
      .catch(() => { /* silent */ });
  }, [isAdmin]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [servicesRes, ordersRes, pettyCashRes] = await Promise.all([
        getServices(),
        getTodayOrders(),
        getPettyCashBalance(),
      ]);
      setServices(servicesRes.data);
      setOrders(ordersRes.data);
      setPettyCashBalance(pettyCashRes.data.balance);
    } catch (error) {
      toast.error('Error al cargar datos');
    }
    setLoading(false);
  };

  // Petty Cash functions
  const fetchPettyCashData = async () => {
    setPettyCashLoading(true);
    try {
      const [balanceRes, movementsRes] = await Promise.all([
        getPettyCashBalance(),
        getPettyCashMovements(),
      ]);
      setPettyCashBalance(balanceRes.data.balance);
      setPettyCashMovements(movementsRes.data.movements);
    } catch (error) {
      // ignored
    }
    setPettyCashLoading(false);
  };

  const handleOpenPettyCash = () => {
    fetchPettyCashData();
    setShowPettyCashModal(true);
  };

  const handlePettyCashSubmit = async (e) => {
    e.preventDefault();
    if (!pettyCashForm.amount || !pettyCashForm.concept) {
      toast.error('Completa todos los campos');
      return;
    }

    try {
      const res = await createPettyCashMovement({
        type: pettyCashForm.type,
        amount: parseFloat(pettyCashForm.amount),
        concept: pettyCashForm.concept,
        notes: pettyCashForm.notes || null,
      });

      toast.success('Movimiento registrado');
      setPettyCashBalance(res.data.new_balance);
      setPettyCashForm({ type: 'ingreso', amount: '', concept: '', notes: '' });
      fetchPettyCashData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar movimiento');
    }
  };

  // Client search
  const handleSearchClient = async (query) => {
    setClientSearch(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const res = await searchClients(query);
      setSearchResults(res.data);
    } catch (error) {
      // ignored
    }
    setSearching(false);
  };

  // Select client
  const handleSelectClient = async (client) => {
    setSelectedClient(client);
    setClientSearch('');
    setSearchResults([]);
    setSelectedReward(null);

    try {
      const [vehiclesRes, rewardsRes] = await Promise.all([
        getClientVehicles(client.id),
        getClientRewards(client.id).catch(() => ({ data: { rewards: [], client_points: 0 } })),
      ]);
      setClientVehicles(vehiclesRes.data);
      if (vehiclesRes.data.length === 1) {
        setSelectedVehicle(vehiclesRes.data[0]);
      }
      setClientRewards(rewardsRes.data.rewards || []);
      if (rewardsRes.data.client_points !== undefined) {
        setSelectedClient((prev) => ({ ...prev, points: rewardsRes.data.client_points }));
      }
    } catch (error) {
      // ignored
    }
  };

  // Create new client
  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      const res = await createClient(newClientData);
      toast.success('Cliente creado');
      handleSelectClient(res.data);
      setShowNewClientModal(false);
      setNewClientData({ name: '', phone: '', email: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear cliente');
    }
  };

  // Create new vehicle
  const handleCreateVehicle = async (e) => {
    e.preventDefault();
    if (!selectedClient) return;

    try {
      const res = await createVehicle({
        ...newVehicleData,
        client_id: selectedClient.id,
      });
      toast.success('Vehículo agregado');
      setClientVehicles([...clientVehicles, res.data]);
      setSelectedVehicle(res.data);
      setShowNewVehicleModal(false);
      setNewVehicleData({ type: 'sedan', model: '', plates: '', color: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear vehículo');
    }
  };

  // Toggle service selection
  const toggleService = (service) => {
    const exists = selectedServices.find((s) => s.id === service.id);
    if (exists) {
      setSelectedServices(selectedServices.filter((s) => s.id !== service.id));
    } else {
      setSelectedServices([...selectedServices, service]);
    }
  };

  // Totals (includes ticketVehicles + current selection)
  const currentSubtotal = selectedServices.reduce((sum, s) => sum + s.price, 0);
  const ticketExtraSubtotal = ticketVehicles.reduce(
    (sum, tv) => sum + tv.services.reduce((a, b) => a + b.price, 0),
    0
  );
  const subtotal = currentSubtotal + ticketExtraSubtotal;
  const rewardDiscount = selectedReward ? selectedReward.discount_value : 0;
  const total = Math.max(0, subtotal - rewardDiscount);

  // Helper: build a human label for a vehicle in the ticket
  const buildVehicleLabel = (clientLike, vehicle) => {
    if (clientLike?.walk_in) {
      const parts = [
        getVehicleTypeLabel(clientLike.vehicle_type) || 'Vehículo',
        clientLike.vehicle_color,
        clientLike.vehicle_model,
      ].filter(Boolean);
      return parts.join(' · ');
    }
    if (vehicle) {
      const parts = [getVehicleTypeLabel(vehicle.type), vehicle.color, vehicle.model, vehicle.plates ? `[${vehicle.plates}]` : null].filter(Boolean);
      return parts.join(' · ');
    }
    return 'Vehículo';
  };

  // "Agregar otro vehículo": move current selection into ticketVehicles list
  const handleAddAnotherVehicle = () => {
    const isWalkIn = selectedClient?.walk_in;
    if (!isWalkIn && (!selectedClient || !selectedVehicle)) {
      toast.error('Selecciona vehículo antes de agregarlo al ticket');
      return;
    }
    if (selectedServices.length === 0) {
      toast.error('Selecciona al menos un servicio para este vehículo');
      return;
    }
    const label = buildVehicleLabel(selectedClient, selectedVehicle);
    const entry = {
      key: `tv-${Date.now()}`,
      label,
      vehicle: isWalkIn ? null : selectedVehicle,
      vehicle_id: isWalkIn ? null : selectedVehicle?.id || null,
      walk_in: isWalkIn,
      walk_in_meta: isWalkIn
        ? {
            name: selectedClient.name,
            type: selectedClient.vehicle_type,
            model: selectedClient.vehicle_model,
            color: selectedClient.vehicle_color,
          }
        : null,
      services: selectedServices.map((s) => ({ id: s.id, name: s.name, price: s.price })),
    };
    setTicketVehicles((prev) => [...prev, entry]);
    // Clear current vehicle + services to let user add another
    setSelectedVehicle(null);
    setSelectedServices([]);
    toast.success(`"${label}" agregado al ticket`);
  };

  const removeTicketVehicle = (key) => {
    setTicketVehicles((prev) => prev.filter((tv) => tv.key !== key));
  };

  // Create order
  const handleCreateOrder = async () => {
    if (!isAdmin && !cashSession) {
      toast.error('Abre la caja antes de cobrar');
      return;
    }
    const isWalkIn = selectedClient?.walk_in;
    const hasCurrent = selectedServices.length > 0;
    const hasExtras = ticketVehicles.length > 0;

    if (!hasCurrent && !hasExtras) {
      toast.error('Selecciona al menos un servicio');
      return;
    }
    if (hasCurrent && !isWalkIn && (!selectedClient || !selectedVehicle)) {
      toast.error('Selecciona el vehículo o usa Walk-in');
      return;
    }

    try {
      // Build flat services array; first vehicle = current selection (if any), else first ticketVehicle
      const flatServices = [];

      if (hasCurrent) {
        const label = buildVehicleLabel(selectedClient, selectedVehicle);
        const vid = isWalkIn ? null : selectedVehicle?.id || null;
        selectedServices.forEach((s) => {
          flatServices.push({ service_id: s.id, price: s.price, vehicle_label: label, vehicle_id: vid });
        });
      }
      ticketVehicles.forEach((tv) => {
        tv.services.forEach((s) => {
          flatServices.push({ service_id: s.id, price: s.price, vehicle_label: tv.label, vehicle_id: tv.vehicle_id });
        });
      });

      // Primary vehicle for the order header: prefer current; else first ticketVehicle
      const primaryIsWalkIn = hasCurrent ? isWalkIn : ticketVehicles[0].walk_in;
      const primaryVehicleId = hasCurrent
        ? (isWalkIn ? null : selectedVehicle?.id)
        : ticketVehicles[0].vehicle_id;
      const walkInMeta = primaryIsWalkIn
        ? (hasCurrent
            ? { name: selectedClient.name, model: selectedClient.vehicle_model, type: selectedClient.vehicle_type, color: selectedClient.vehicle_color }
            : ticketVehicles[0].walk_in_meta)
        : null;

      const orderData = {
        client_id: selectedClient?.walk_in ? null : selectedClient?.id,
        vehicle_id: primaryVehicleId,
        services: flatServices,
        notes: notes || null,
        estimated_time: estimatedTime || null,
        reward_id: selectedClient?.walk_in ? null : selectedReward?.id || null,
        walk_in_name: walkInMeta?.name || null,
        walk_in_vehicle: walkInMeta?.model || null,
        walk_in_vehicle_type: walkInMeta?.type || null,
        walk_in_vehicle_color: walkInMeta?.color || null,
      };

      const res = await createOrder(orderData);
      setCurrentOrderId(res.data.id);
      setShowCheckoutModal(true);
      toast.success(`Orden creada · ${ticketVehicles.length + (hasCurrent ? 1 : 0)} vehículo(s)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear orden');
    }
  };

  // Checkout
  const handleCheckout = async () => {
    if (!currentOrderId) return;

    setCheckoutLoading(true);
    try {
      const res = await checkoutOrder(currentOrderId, {
        payment_method: paymentMethod,
        discount: parseFloat(discount) || 0,
        points_to_redeem: parseInt(pointsToRedeem) || 0,
      });

      const isWalkInTicket = selectedClient?.walk_in === true;
      // Build combined services list with vehicle_label for multi-vehicle tickets
      const allTicketServices = [];
      if (selectedServices.length > 0) {
        const label = buildVehicleLabel(selectedClient, selectedVehicle);
        selectedServices.forEach((s) => allTicketServices.push({ ...s, vehicle_label: label }));
      }
      ticketVehicles.forEach((tv) => {
        tv.services.forEach((s) => allTicketServices.push({ ...s, vehicle_label: tv.label }));
      });

      setTicketData({
        folio: res.data.folio,
        client: selectedClient,
        walk_in: isWalkInTicket,
        vehicle: isWalkInTicket
          ? (selectedClient?.vehicle_type || selectedClient?.vehicle_model || selectedClient?.vehicle_color)
            ? {
                type: selectedClient.vehicle_type || 'otro',
                model: selectedClient.vehicle_model || null,
                color: selectedClient.vehicle_color || null,
                plates: null,
              }
            : null
          : selectedVehicle,
        services: allTicketServices,
        multi_vehicle: ticketVehicles.length > 0,
        total: res.data.total,
        points_earned: res.data.points_earned,
        points_redeemed: res.data.points_redeemed,
        payment_method: paymentMethod,
        date: new Date().toLocaleString('es-MX'),
      });

      setShowCheckoutModal(false);
      setShowTicketModal(true);

      resetOrder();
      fetchInitialData();

      toast.success('Pago procesado exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar pago');
    }
    setCheckoutLoading(false);
  };

  const resetOrder = () => {
    setSelectedClient(null);
    setSelectedVehicle(null);
    setSelectedServices([]);
    setTicketVehicles([]);
    setClientVehicles([]);
    setNotes('');
    setEstimatedTime('');
    setCurrentOrderId(null);
    setPaymentMethod('efectivo');
    setDiscount(0);
    setPointsToRedeem(0);
  };

  // Charge an existing pending order from the orders table
  const handleChargeExistingOrder = (order) => {
    // Hydrate the POS state so the checkout modal has the right client/services
    setSelectedClient(
      order.client
        ? {
            id: order.client.id || null,
            name: order.client.name,
            phone: order.client.phone,
            points: 0,
            walk_in: !!order.client.walk_in || !order.client.id,
          }
        : { id: null, walk_in: true, name: 'Walk-in', phone: '—', points: 0 },
    );
    setSelectedVehicle(order.vehicle?.id ? order.vehicle : null);
    setSelectedServices((order.services || []).map((s, idx) => ({
      id: s.service_id || `line-${idx}`,
      name: s.name,
      price: s.price,
    })));
    setSelectedReward(null);
    setCurrentOrderId(order.id);
    setDiscount(0);
    setPointsToRedeem(0);
    setPaymentMethod('efectivo');
    setShowCheckoutModal(true);
  };

  // Update order status
  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      await updateOrderStatus(orderId, newStatus);
      setOrders(orders.map((o) => (o.id === orderId ? { ...o, service_status: newStatus } : o)));
      toast.success('Estado actualizado');
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  // Delete order (soft delete, admin only)
  const handleDeleteOrder = async (orderId, reason) => {
    const res = await deleteOrder(orderId, reason);
    setOrders((prev) => prev.filter((o) => o.id !== orderId));
    toast.success(`Venta ${res.data?.folio || ''} eliminada`);
    return res;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fadeIn">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">POS / Caja</h1>
            <p className="text-slate-600 mt-1">Crear nueva orden de servicio</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {isAdmin && branches.length > 0 && (
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className="h-10 px-3 border border-slate-200 rounded-lg text-sm bg-white"
                data-testid="pos-branch-select"
              >
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            )}
            <CashSessionBar
              branchId={isAdmin ? selectedBranch : null}
              onSessionChange={setCashSession}
            />
            <Button
              onClick={handleOpenPettyCash}
              variant="outline"
              className="gap-2 border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700"
              data-testid="petty-cash-btn"
            >
              <Wallet className="w-4 h-4" />
              Caja Chica
              <span className="font-bold">{formatCurrency(pettyCashBalance)}</span>
            </Button>
            <Button onClick={fetchInitialData} variant="outline" className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Actualizar
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left: Service Selection */}
          <div className="lg:col-span-8 space-y-6">
            {/* Client Selection */}
            <div className="card-base p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                Cliente
              </h2>

              {!selectedClient ? (
                <div className="space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      placeholder="Buscar por nombre, teléfono o código..."
                      className="pl-10 h-14"
                      value={clientSearch}
                      onChange={(e) => handleSearchClient(e.target.value)}
                      data-testid="client-search-input"
                    />
                  </div>

                  {searching && (
                    <div className="flex items-center gap-2 text-slate-500">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Buscando...
                    </div>
                  )}

                  {searchResults.length > 0 && (
                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                      {searchResults.map((client) => (
                        <button
                          key={client.id}
                          className="w-full p-4 text-left hover:bg-slate-50 border-b border-slate-100 last:border-0 flex items-center justify-between"
                          onClick={() => handleSelectClient(client)}
                          data-testid={`client-result-${client.id}`}
                        >
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-900">{client.name}</span>
                              {client.code && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-mono font-bold rounded">
                                  {client.code}
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-slate-500">{client.phone}</div>
                          </div>
                          <div className="flex items-center gap-1 text-amber-600">
                            <Star className="w-4 h-4" />
                            <span className="font-semibold">{client.points}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      variant="outline"
                      className="h-14 gap-2"
                      onClick={() => setShowNewClientModal(true)}
                      data-testid="new-client-btn"
                    >
                      <Plus className="w-5 h-5" />
                      Nuevo Cliente
                    </Button>
                    <Button
                      variant="outline"
                      className="h-14 gap-2 border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700"
                      onClick={() => setShowWalkInModal(true)}
                      data-testid="walk-in-btn"
                    >
                      <User className="w-5 h-5" />
                      Walk-in (sin registro)
                    </Button>
                  </div>
                </div>
              ) : (
                <div className={`flex items-center justify-between p-4 rounded-xl ${selectedClient.walk_in ? 'bg-amber-50 border border-amber-200' : 'bg-blue-50'}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900">{selectedClient.name}</span>
                      {selectedClient.walk_in && (
                        <span className="px-2 py-0.5 bg-amber-500 text-white text-xs font-bold rounded">
                          WALK-IN
                        </span>
                      )}
                      {selectedClient.code && (
                        <span className="px-2 py-0.5 bg-blue-600 text-white text-xs font-mono font-bold rounded">
                          {selectedClient.code}
                        </span>
                      )}
                    </div>
                    {!selectedClient.walk_in && (
                      <>
                        <div className="text-sm text-slate-600 flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          {selectedClient.phone}
                        </div>
                        <div className="text-sm text-amber-600 flex items-center gap-1 mt-1">
                          <Star className="w-4 h-4" />
                          {selectedClient.points} puntos
                        </div>
                      </>
                    )}
                    {selectedClient.walk_in && (
                      <div className="text-xs text-amber-700 mt-1">
                        Sin registro · no acumula puntos
                      </div>
                    )}
                    {selectedClient.walk_in && (selectedClient.vehicle_type || selectedClient.vehicle_model || selectedClient.vehicle_color) && (
                      <div className="mt-2 text-sm text-slate-700 flex items-center gap-1" data-testid="walk-in-vehicle-info">
                        <Car className="w-4 h-4 text-amber-600" />
                        <span>
                          {getVehicleTypeLabel(selectedClient.vehicle_type)}
                          {selectedClient.vehicle_color ? ` · ${selectedClient.vehicle_color}` : ''}
                          {selectedClient.vehicle_model ? ` · ${selectedClient.vehicle_model}` : ''}
                        </span>
                      </div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setSelectedClient(null);
                      setSelectedVehicle(null);
                      setClientVehicles([]);
                    }}
                    data-testid="clear-client-btn"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              )}
            </div>

            {/* Vehicle Selection */}
            {selectedClient && !selectedClient.walk_in && (
              <div className="card-base p-6 animate-fadeIn">
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Car className="w-5 h-5 text-blue-600" />
                  Vehículo
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {clientVehicles.map((vehicle) => (
                    <button
                      key={vehicle.id}
                      className={`p-4 border-2 rounded-xl text-left transition-all ${
                        selectedVehicle?.id === vehicle.id
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-slate-200 hover:border-blue-400'
                      }`}
                      onClick={() => setSelectedVehicle(vehicle)}
                      data-testid={`vehicle-${vehicle.id}`}
                    >
                      <div className="font-medium text-slate-900">
                        {getVehicleTypeLabel(vehicle.type)}
                      </div>
                      {vehicle.model && <div className="text-sm text-slate-600">{vehicle.model}</div>}
                      {vehicle.plates && (
                        <div className="text-sm text-slate-500 font-mono">{vehicle.plates}</div>
                      )}
                    </button>
                  ))}

                  <button
                    className="p-4 border-2 border-dashed border-slate-300 rounded-xl text-slate-500 hover:border-blue-400 hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
                    onClick={() => setShowNewVehicleModal(true)}
                    data-testid="new-vehicle-btn"
                  >
                    <Plus className="w-5 h-5" />
                    Nuevo Vehículo
                  </button>
                </div>
              </div>
            )}

            {/* Services Grid */}
            <div className="card-base p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Servicios</h2>

              {/* Category Filter */}
              <div className="flex flex-wrap gap-2 mb-4" data-testid="service-category-filter">
                {['all', ...new Set(services.map((s) => s.category).filter(Boolean))].map((cat) => (
                  <button
                    key={cat}
                    className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${
                      serviceCategory === cat
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    onClick={() => setServiceCategory(cat)}
                    data-testid={`category-filter-${cat}`}
                  >
                    {cat === 'all' ? 'Todos' : cat}
                  </button>
                ))}
              </div>

              {/* Services grouped by subcategory */}
              {(() => {
                const filtered =
                  serviceCategory === 'all'
                    ? services
                    : services.filter((s) => s.category === serviceCategory);
                const grouped = {};
                filtered.forEach((s) => {
                  const sub = s.subcategory || 'General';
                  if (!grouped[sub]) grouped[sub] = [];
                  grouped[sub].push(s);
                });
                return Object.entries(grouped).map(([sub, svcs]) => (
                  <div key={sub} className="mb-4">
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">
                      {sub}
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {svcs.map((service) => {
                        const isSelected = selectedServices.some((s) => s.id === service.id);
                        return (
                          <button
                            key={service.id}
                            className={`service-card ${isSelected ? 'active' : ''}`}
                            onClick={() => toggleService(service)}
                            data-testid={`service-btn-${service.id}`}
                          >
                            <div>
                              <div className="font-semibold text-slate-900 text-sm">
                                {service.name}
                              </div>
                              <div className="text-xs text-slate-500 mt-1">
                                {service.duration} min
                              </div>
                            </div>
                            <div className="text-lg font-bold text-blue-600 tabular-nums">
                              {formatCurrency(service.price)}
                            </div>
                            {isSelected && (
                              <div className="absolute top-2 right-2">
                                <CheckCircle className="w-5 h-5 text-blue-600" />
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ));
              })()}
            </div>

            {/* Notes */}
            <div className="card-base p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Información Adicional</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="notes">Notas</Label>
                  <Textarea
                    id="notes"
                    placeholder="Instrucciones especiales..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="mt-1"
                    data-testid="order-notes"
                  />
                </div>
                <div>
                  <Label htmlFor="estimated">Hora Estimada</Label>
                  <Input
                    id="estimated"
                    type="time"
                    value={estimatedTime}
                    onChange={(e) => setEstimatedTime(e.target.value)}
                    className="mt-1 h-14"
                    data-testid="order-estimated-time"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right: Ticket */}
          <div className="lg:col-span-4">
            <div className="card-base p-6 sticky top-4">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Receipt className="w-5 h-5 text-blue-600" />
                Ticket
              </h2>

              {selectedServices.length === 0 && ticketVehicles.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Car className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Selecciona servicios para continuar</p>
                </div>
              ) : (
                <>
                  {/* Vehículos ya agregados al ticket */}
                  {ticketVehicles.length > 0 && (
                    <div className="space-y-3 mb-3" data-testid="ticket-vehicles-list">
                      {ticketVehicles.map((tv, idx) => {
                        const tvSubtotal = tv.services.reduce((a, b) => a + b.price, 0);
                        return (
                          <div key={tv.key} className="rounded-lg border border-slate-200 bg-slate-50" data-testid={`ticket-vehicle-${idx}`}>
                            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200">
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center shrink-0">
                                  {idx + 1}
                                </div>
                                <span className="text-sm font-semibold text-slate-800 truncate">{tv.label}</span>
                              </div>
                              <button
                                type="button"
                                className="text-red-500 hover:text-red-700 shrink-0"
                                onClick={() => removeTicketVehicle(tv.key)}
                                data-testid={`remove-ticket-vehicle-${idx}`}
                                aria-label="Quitar vehículo"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="px-3 py-2 space-y-1">
                              {tv.services.map((s) => (
                                <div key={s.id} className="flex items-center justify-between text-sm">
                                  <span className="text-slate-700">{s.name}</span>
                                  <span className="font-semibold tabular-nums">{formatCurrency(s.price)}</span>
                                </div>
                              ))}
                              <div className="flex items-center justify-between pt-1 border-t border-slate-200 text-sm">
                                <span className="text-slate-500">Subtotal</span>
                                <span className="font-bold tabular-nums">{formatCurrency(tvSubtotal)}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Servicios del vehículo en edición */}
                  {selectedServices.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {ticketVehicles.length > 0 && (
                        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center">{ticketVehicles.length + 1}</span>
                          Vehículo actual ({buildVehicleLabel(selectedClient, selectedVehicle)})
                        </div>
                      )}
                      {selectedServices.map((service) => (
                        <div
                          key={service.id}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                        >
                          <span className="font-medium text-slate-700">{service.name}</span>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold tabular-nums">
                              {formatCurrency(service.price)}
                            </span>
                            <button
                              onClick={() => toggleService(service)}
                              className="text-red-500 hover:text-red-700"
                              data-testid={`remove-service-${service.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Botón Agregar otro vehículo */}
                  {(selectedServices.length > 0 || ticketVehicles.length > 0) && (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full mb-3 border-dashed border-blue-300 text-blue-700 hover:bg-blue-50"
                      onClick={handleAddAnotherVehicle}
                      disabled={selectedServices.length === 0}
                      data-testid="add-another-vehicle-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Agregar otro vehículo al ticket
                    </Button>
                  )}

                  <div className="border-t border-slate-200 pt-4">
                    <div className="flex items-center justify-between text-2xl font-bold">
                      <span>Total</span>
                      <div className="flex items-center gap-2">
                        {selectedReward && (
                          <span className="text-slate-400 line-through text-lg tabular-nums">
                            {formatCurrency(subtotal)}
                          </span>
                        )}
                        <span
                          className={`tabular-nums ${selectedReward ? 'text-emerald-600' : 'text-blue-600'}`}
                        >
                          {formatCurrency(total)}
                        </span>
                      </div>
                    </div>

                    {selectedClient && !selectedReward && (
                      <div className="mt-2 text-sm text-amber-600 flex items-center gap-1">
                        <Star className="w-4 h-4" />
                        Ganará {Math.floor(total)} puntos
                      </div>
                    )}
                    {selectedReward && (
                      <div className="mt-2 text-sm text-emerald-600 flex items-center gap-1">
                        <Gift className="w-4 h-4" />
                        Recompensa: {selectedReward.name} (-{formatCurrency(selectedReward.discount_value)},
                        -{selectedReward.points_required} pts)
                      </div>
                    )}
                  </div>

                  {/* Available Rewards */}
                  {selectedClient && clientRewards.length > 0 && (
                    <div
                      className="mt-4 p-3 bg-emerald-50 rounded-xl border border-emerald-200"
                      data-testid="rewards-section"
                    >
                      <h4 className="text-sm font-semibold text-emerald-800 mb-2 flex items-center gap-2">
                        <Gift className="w-4 h-4" />
                        Recompensas ({selectedClient.points} pts)
                      </h4>
                      <div className="space-y-2">
                        {clientRewards
                          .filter((r) => r.can_redeem)
                          .map((reward) => (
                            <button
                              key={reward.id}
                              type="button"
                              className={`w-full text-left p-2 rounded-lg border-2 transition-all text-sm ${
                                selectedReward?.id === reward.id
                                  ? 'border-emerald-500 bg-emerald-100'
                                  : 'border-emerald-200 bg-white hover:border-emerald-400'
                              }`}
                              onClick={() =>
                                setSelectedReward(selectedReward?.id === reward.id ? null : reward)
                              }
                              data-testid={`reward-btn-${reward.id}`}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="font-medium text-emerald-900">{reward.name}</div>
                                  {reward.description && (
                                    <div className="text-xs text-emerald-600">{reward.description}</div>
                                  )}
                                </div>
                                <div className="text-right">
                                  <div className="text-xs font-semibold text-emerald-700 bg-emerald-200 px-2 py-1 rounded-full">
                                    {reward.points_required} pts
                                  </div>
                                  <div className="text-xs text-emerald-800 font-bold mt-1">
                                    -{formatCurrency(reward.discount_value)}
                                  </div>
                                </div>
                              </div>
                            </button>
                          ))}
                        {clientRewards.filter((r) => r.can_redeem).length === 0 && (
                          <p className="text-xs text-emerald-600">
                            Aún no tiene suficientes puntos para canjear
                          </p>
                        )}
                      </div>
                      {selectedReward && (
                        <div className="mt-2 p-2 bg-emerald-200 rounded-lg text-center">
                          <span className="text-sm font-bold text-emerald-900">
                            Descuento de {formatCurrency(selectedReward.discount_value)} aplicado
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <Button
                    className="pos-btn mt-6"
                    onClick={handleCreateOrder}
                    disabled={
                      !selectedClient ||
                      (selectedServices.length === 0 && ticketVehicles.length === 0) ||
                      (selectedServices.length > 0 && !selectedClient.walk_in && !selectedVehicle)
                    }
                    data-testid="checkout-btn"
                  >
                    <CreditCard className="w-5 h-5" />
                    {selectedReward ? `Cobrar ${formatCurrency(total)} (Desc. aplicado)` : 'Cobrar'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Today's Orders */}
        <div className="card-base p-6">
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Órdenes del Día</h2>
          <OrdersTable
            orders={orders}
            onStatusChange={handleUpdateStatus}
            onCharge={handleChargeExistingOrder}
            onDelete={handleDeleteOrder}
            canDelete={isAdmin}
          />
        </div>
      </div>

      {/* Modals */}
      <NewClientModal
        open={showNewClientModal}
        onOpenChange={setShowNewClientModal}
        data={newClientData}
        onChange={setNewClientData}
        onSubmit={handleCreateClient}
      />

      <NewVehicleModal
        open={showNewVehicleModal}
        onOpenChange={setShowNewVehicleModal}
        data={newVehicleData}
        onChange={setNewVehicleData}
        onSubmit={handleCreateVehicle}
      />

      <CheckoutModal
        open={showCheckoutModal}
        onOpenChange={setShowCheckoutModal}
        total={total}
        discount={discount}
        setDiscount={setDiscount}
        pointsToRedeem={pointsToRedeem}
        setPointsToRedeem={setPointsToRedeem}
        paymentMethod={paymentMethod}
        setPaymentMethod={setPaymentMethod}
        selectedClient={selectedClient}
        onConfirm={handleCheckout}
        loading={checkoutLoading}
      />

      <TicketModal
        open={showTicketModal}
        onOpenChange={setShowTicketModal}
        ticketData={ticketData}
      />

      <PettyCashModal
        open={showPettyCashModal}
        onOpenChange={setShowPettyCashModal}
        balance={pettyCashBalance}
        movements={pettyCashMovements}
        loading={pettyCashLoading}
        form={pettyCashForm}
        setForm={setPettyCashForm}
        onSubmit={handlePettyCashSubmit}
      />

      <WalkInModal
        open={showWalkInModal}
        onOpenChange={setShowWalkInModal}
        onConfirm={(data) => {
          setSelectedClient({
            id: null,
            walk_in: true,
            name: data.name,
            phone: '—',
            points: 0,
            vehicle_type: data.vehicle_type,
            vehicle_model: data.model,
            vehicle_color: data.color,
          });
          setSelectedVehicle(null);
          setClientVehicles([]);
          setClientRewards([]);
          setSelectedReward(null);
          setShowWalkInModal(false);
        }}
      />
    </DashboardLayout>
  );
}
